import {Injectable} from '@angular/core';
import {ToastrService} from "ngx-toastr";
import {NotificationType} from "../enum/notification-type.enum";
import {ResponseMessageConstants} from "../constant/response-message-constants";
import {HttpErrorResponse} from "@angular/common/http";

/**
 * @file NotificationService.ts
 * @author Mohamed Ali Kenis
 * @date 01/31/2023
 * @desc Service for handling notifications using the Toastr library
 */
@Injectable({
  providedIn: 'root'
})
export class NotificationService {

  /**
   * Constructor
   * @param {ToastrService} notifier The Toastr service instance
   */
  constructor(private notifier : ToastrService) { }

  /**
   * Show a notification with the specified type and message
   * @param {NotificationType} type The type of notification (success, error, info, warning)
   * @param {string} message The message to show in the notification
   * @param {string} title The title of the notification
   */
  public notify(type: NotificationType, message: string, title: string) {
    switch (type) {
      case NotificationType.SUCCESS:
        this.notifier.success(message, title);
        break;
      case NotificationType.ERROR:
        this.notifier.error(message, title);
        break;
      case NotificationType.INFO:
        this.notifier.info(message, title);
        break;
      case NotificationType.WARNING:
        this.notifier.warning(message, title);
        break;
    }
  }
  sendNotification(notificationType: NotificationType, message: string, title: string): void {
    if (message) {
      this.notify(
        notificationType,
        message,
        title);
    } else {
      this.notify(
        notificationType,
        ResponseMessageConstants.DEFAULT_ERROR.message,
        ResponseMessageConstants.DEFAULT_ERROR.title
      );
    }
  }

  errorHandler(httpErrorResponse: HttpErrorResponse): void {
    // 1. Erreur réseau (pas de réponse)
    if (!httpErrorResponse.error || httpErrorResponse.status === 0) {
      this.sendNotification(
        NotificationType.ERROR,
        'Impossible de contacter le serveur. Vérifiez votre connexion.',
        'Erreur réseau'
      );
      return;
    }

    const error = httpErrorResponse.error;
    const status = httpErrorResponse.status;

    // 2. Cas particulier : 404
    if (status === 404) {
      this.sendNotification(NotificationType.WARNING, error.message || 'Ressource non trouvée', '404');
      return;
    }

    // 3. Erreurs de validation (le plus important !)
    if (error.details) {
      const details = error.details;

      // Cas A : tableau de violations (plusieurs erreurs)
      if (Array.isArray(details)) {
        details.forEach((detail: any) => {
          if (detail.message) {
            const msg = detail.message
            this.sendNotification(NotificationType.ERROR, msg,'');
          }
        });
        return;
      }

      // Cas B : une seule violation (objet)
      if (typeof details === 'object' && details.message) {
        const field = details.field ? `${this.capitalize(details.field)} : ` : '';
        this.sendNotification(NotificationType.ERROR, field + details.message,'');
        return;
      }
    }

    // 4. Message générique du backend (ex: "Can't process entity...")
    if (error.message) {
      this.sendNotification(NotificationType.ERROR, error.message,'');
      return;
    }

    // 5. Fallback ultime
    this.sendNotification(
      NotificationType.ERROR,
      `Erreur ${status} : Une erreur inattendue est survenue`,
      'Erreur serveur'
    );
  }
  private capitalize(str: string): string {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  }
}
